import React, { useState, useEffect } from 'react';
import ExpiryBanner from './ExpiryBanner';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Drawer,
    Popover,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    alpha,
    Avatar,
    Badge,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    Menu as MenuIcon,
    NotificationsNone as NotificationsIcon,
    Brightness4 as Brightness4Icon,
    Brightness7 as Brightness7Icon,
    Settings as SettingsIcon,
    HelpOutline as HelpIcon,
    Logout as LogoutIcon,
    ExpandMore as ExpandMoreIcon,
    CreditCard as BillingIcon,
    Star as PricingIcon,
    AdminPanelSettings as AdminIcon,
    ChevronLeft as CollapseIcon,
    ChevronRight as ExpandIcon,
} from '@mui/icons-material';
import Navigation from './Navigation';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { FEATURES } from '../constants/features';

const SIDEBAR_WIDTH = 280;

const MainLayout = ({ children, isDarkMode, onToggleTheme }) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        () => localStorage.getItem('sidebarCollapsed') === 'true'
    );

    const toggleSidebar = () => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('sidebarCollapsed', String(next));
            return next;
        });
    };

    const [currentUser, setCurrentUser] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [adminAnchorEl, setAdminAnchorEl] = useState(null);
    const [userPlan, setUserPlan] = useState(null);

    const { isFeatureEnabled } = useFeatureToggles();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        handleMenuClose();
        try {
            await signOut(auth);
            navigate('/');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                try {
                    const token = await user.getIdTokenResult();
                    setIsSuperAdmin(!!token.claims.super_admin);
                    setUserPlan(token.claims.plan || 'free');
                } catch (err) { }
            } else {
                setIsSuperAdmin(false);
            }
        });
        return () => unsubscribe();
    }, []);

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const sidebarContent = (onCloseSidebar) => (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
            {/* Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6, px: 1 }}>
                <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.5)' }}>
                    <Box
                        component="img"
                        src="/logo192.png"
                        alt="Socialixer"
                        sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                    />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.02em', color: theme.palette.text.primary, flex: 1 }}>
                    Socialixer
                </Typography>
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onCloseSidebar(); }}
                    sx={{
                        color: 'text.disabled',
                        position: 'relative',
                        zIndex: 10,
                        '&:hover': { color: 'text.primary', bgcolor: alpha(theme.palette.divider, 0.15) },
                    }}
                    title="Collapse sidebar"
                >
                    <CollapseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Navigation */}
            <Box sx={{
                flexGrow: 1,
                overflowY: 'auto',
                mb: 2,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
            }}>
                <Navigation isSidebar={true} onCloseDrawer={() => setMobileOpen(false)} />
            </Box>

            {/* User Profile Dropdown */}
            <Box sx={{ mt: 'auto', flexShrink: 0 }}>
                <Box
                    onClick={handleMenuOpen}
                    sx={{
                        p: 1.5,
                        borderRadius: 4,
                        background: anchorEl ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.1),
                        }
                    }}
                >
                    <Avatar
                        src={currentUser?.photoURL}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            width: 40,
                            height: 40,
                            fontSize: '0.9rem',
                            fontWeight: 700
                        }}
                    >
                        {getInitials(currentUser?.displayName || currentUser?.email)}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 800, color: 'text.primary' }}>
                            {currentUser?.displayName || 'Active User'}
                        </Typography>
                        <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
                            {currentUser?.email || 'Logged In'}
                        </Typography>
                    </Box>
                    <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </Box>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    PaperProps={{
                        sx: {
                            mt: -1.5,
                            width: 260,
                            borderRadius: '20px',
                            boxShadow: isDarkMode
                                ? '0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)'
                                : '0 24px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
                            border: `1px solid ${alpha(theme.palette.divider, isDarkMode ? 0.1 : 0.12)}`,
                            bgcolor: isDarkMode ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.96)',
                            backdropFilter: 'blur(24px)',
                            overflow: 'hidden',
                            p: 1,
                        }
                    }}
                >
                    {/* User header inside menu */}
                    <Box sx={{
                        mx: 0.5, mb: 1, p: 2, borderRadius: '14px',
                        background: isDarkMode
                            ? 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(168,85,247,0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.05) 100%)',
                        border: `1px solid ${alpha('#6366f1', isDarkMode ? 0.2 : 0.15)}`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                                src={currentUser?.photoURL}
                                sx={{ width: 36, height: 36, bgcolor: '#6366f1', fontSize: '0.8rem', fontWeight: 800 }}
                            >
                                {getInitials(currentUser?.displayName || currentUser?.email)}
                            </Avatar>
                            <Box sx={{ overflow: 'hidden', flex: 1 }}>
                                <Typography noWrap sx={{ fontWeight: 800, fontSize: '0.85rem', lineHeight: 1.2 }}>
                                    {currentUser?.displayName || 'Active User'}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                    {currentUser?.email || 'Logged in'}
                                </Typography>
                            </Box>
                            {userPlan && userPlan !== 'free' && (
                                <Box sx={{ px: 1, py: 0.3, borderRadius: '6px', bgcolor: alpha('#10d98a', 0.15), border: `1px solid ${alpha('#10d98a', 0.3)}` }}>
                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: '#10d98a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pro</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Helper to build a styled menu item */}
                    {[
                        { to: '/app/guide',    icon: <HelpIcon />,     label: 'Setup Guide',          color: '#6366f1' },
                        { to: '/app/settings', icon: <SettingsIcon />, label: 'Settings',             color: '#6366f1' },
                    ].map(({ to, icon, label, color }) => {
                        const active = location.pathname === to;
                        return (
                            <MenuItem
                                key={to}
                                component={Link}
                                to={to}
                                onClick={() => { handleMenuClose(); setMobileOpen(false); }}
                                sx={{
                                    borderRadius: '12px', py: 1, px: 1.5, mb: 0.5, gap: 1.5,
                                    bgcolor: active ? alpha(color, 0.1) : 'transparent',
                                    '&:hover': { bgcolor: alpha(color, isDarkMode ? 0.12 : 0.08) },
                                    transition: 'background 0.15s',
                                }}
                            >
                                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(color, active ? 0.2 : 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {React.cloneElement(icon, { sx: { fontSize: 16, color: active ? color : alpha(color, 0.7) } })}
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: active ? color : 'text.primary' }}>{label}</Typography>
                            </MenuItem>
                        );
                    })}

                    {isSuperAdmin && (() => {
                        const ADMIN_COLOR = '#f59e0b';
                        const adminLinks = [
                            { to: '/app/admin/toggles',          label: 'Admin Settings'   },
                            { to: '/app/admin/plans',            label: 'Plan Management'  },
                            { to: '/app/admin/newsletter',       label: 'Newsletter Admin' },
                            { to: '/app/admin/blog',             label: 'Blog Admin'       },
                            { to: '/app/admin/r2-settings',      label: 'R2 Storage'       },
                            { to: '/app/admin/gateway-settings', label: 'Payment Gateways' },
                            { to: '/app/admin/ai-settings',      label: 'AI Provider'      },
                            { to: '/app/admin/platform-settings', label: 'Platform OAuth'  },
                            { to: '/app/admin/idea-templates',   label: 'Idea Templates'   },
                            { to: '/app/admin/dead-letter',      label: 'Dead-Letter Queue'},
                            { to: '/app/admin/infrastructure',   label: 'Infrastructure'   },
                        ];
                        const anyAdminActive = adminLinks.some(l => location.pathname === l.to);
                        const adminPopoverOpen = Boolean(adminAnchorEl);
                        return (
                            <>
                                <Box sx={{ my: 1, mx: 1, height: '1px', bgcolor: alpha(theme.palette.divider, 0.1) }} />

                                {/* Admin Panel trigger — opens flyout popover */}
                                <MenuItem
                                    onClick={(e) => setAdminAnchorEl(adminAnchorEl ? null : e.currentTarget)}
                                    sx={{
                                        borderRadius: '12px', py: 1, px: 1.5, mb: 0.5, gap: 1.5,
                                        bgcolor: anyAdminActive || adminPopoverOpen ? alpha(ADMIN_COLOR, 0.1) : 'transparent',
                                        '&:hover': { bgcolor: alpha(ADMIN_COLOR, isDarkMode ? 0.12 : 0.08) },
                                    }}
                                >
                                    <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(ADMIN_COLOR, anyAdminActive ? 0.2 : 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <AdminIcon sx={{ fontSize: 16, color: anyAdminActive ? ADMIN_COLOR : alpha(ADMIN_COLOR, 0.7) }} />
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: anyAdminActive ? ADMIN_COLOR : 'text.primary', flex: 1 }}>
                                        Admin Panel
                                    </Typography>
                                    <ExpandMoreIcon sx={{
                                        fontSize: 18,
                                        color: alpha(ADMIN_COLOR, 0.7),
                                        transition: 'transform 0.2s',
                                        transform: adminPopoverOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
                                    }} />
                                </MenuItem>

                                {/* Flyout popover with admin sub-menu */}
                                <Popover
                                    open={adminPopoverOpen}
                                    anchorEl={adminAnchorEl}
                                    onClose={() => setAdminAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                ml: 1,
                                                borderRadius: '14px',
                                                minWidth: 210,
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                                                bgcolor: isDarkMode ? '#1e2233' : '#ffffff',
                                                border: `1px solid ${alpha(ADMIN_COLOR, 0.15)}`,
                                                p: 1,
                                            },
                                        },
                                    }}
                                >
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: alpha(ADMIN_COLOR, 0.7), textTransform: 'uppercase', letterSpacing: '0.08em', px: 1.5, pt: 0.5, pb: 0.5 }}>
                                        Admin Panel
                                    </Typography>
                                    {adminLinks.map(({ to, label }) => {
                                        const active = location.pathname === to;
                                        return (
                                            <MenuItem
                                                key={to}
                                                component={Link}
                                                to={to}
                                                onClick={() => { setAdminAnchorEl(null); handleMenuClose(); setMobileOpen(false); }}
                                                sx={{
                                                    borderRadius: '10px', py: 0.9, px: 1.5, mb: 0.25, gap: 1.5,
                                                    bgcolor: active ? alpha(ADMIN_COLOR, 0.12) : 'transparent',
                                                    '&:hover': { bgcolor: alpha(ADMIN_COLOR, isDarkMode ? 0.12 : 0.08) },
                                                }}
                                            >
                                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: active ? ADMIN_COLOR : alpha(ADMIN_COLOR, 0.35), flexShrink: 0 }} />
                                                <Typography sx={{ fontWeight: active ? 700 : 500, fontSize: '0.85rem', color: active ? ADMIN_COLOR : 'text.primary' }}>
                                                    {label}
                                                </Typography>
                                            </MenuItem>
                                        );
                                    })}
                                </Popover>
                            </>
                        );
                    })()}

                    {isFeatureEnabled(FEATURES.BILLING) && (
                        <>
                            <Box sx={{ my: 1, mx: 1, height: '1px', bgcolor: alpha(theme.palette.divider, 0.1) }} />
                            {[
                                { to: '/app/pricing', icon: <PricingIcon />, label: 'Pricing & Plans',       color: '#10d98a' },
                                { to: '/app/billing', icon: <BillingIcon />, label: 'Billing & Subscription', color: '#10d98a' },
                            ].map(({ to, icon, label, color }) => {
                                const active = location.pathname === to;
                                return (
                                    <MenuItem
                                        key={to}
                                        component={Link}
                                        to={to}
                                        onClick={() => { handleMenuClose(); setMobileOpen(false); }}
                                        sx={{
                                            borderRadius: '12px', py: 1, px: 1.5, mb: 0.5, gap: 1.5,
                                            bgcolor: active ? alpha(color, 0.1) : 'transparent',
                                            '&:hover': { bgcolor: alpha(color, isDarkMode ? 0.12 : 0.08) },
                                        }}
                                    >
                                        <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(color, active ? 0.2 : 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            {React.cloneElement(icon, { sx: { fontSize: 16, color: active ? color : alpha(color, 0.7) } })}
                                        </Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: active ? color : 'text.primary' }}>{label}</Typography>
                                    </MenuItem>
                                );
                            })}
                        </>
                    )}

                    <Box sx={{ my: 1, mx: 1, height: '1px', bgcolor: alpha(theme.palette.divider, 0.1) }} />
                    <MenuItem
                        onClick={handleLogout}
                        sx={{
                            borderRadius: '12px', py: 1, px: 1.5, gap: 1.5,
                            '&:hover': { bgcolor: alpha('#ef4444', 0.08) },
                        }}
                    >
                        <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: alpha('#ef4444', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <LogoutIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#ef4444' }}>Log out</Typography>
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    );

    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            bgcolor: theme.palette.background.default,
            backgroundImage: isDarkMode ?
                'radial-gradient(circle at 15% 50%, rgba(34, 197, 94, 0.08), transparent 35%), radial-gradient(circle at 85% 30%, rgba(99, 102, 241, 0.08), transparent 35%)'
                : 'none',
            backgroundAttachment: 'fixed',
        }}>
            {/* Sidebar - Desktop */}
            {!isMobile && (
                <Box
                    component="nav"
                    sx={{
                        width: sidebarCollapsed ? 72 : SIDEBAR_WIDTH,
                        flexShrink: 0,
                        borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.4),
                        backdropFilter: 'blur(20px)',
                        position: 'fixed',
                        height: '100vh',
                        zIndex: theme.zIndex.appBar + 1,
                        transition: 'width 0.3s ease',
                        overflow: 'hidden',
                        transform: 'translateZ(0)',
                        willChange: 'width',
                    }}
                >
                    {sidebarCollapsed ? (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 2 }}>
                            {/* Mini logo */}
                            <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.5)', mb: 1 }}>
                                <Box
                                    component="img"
                                    src="/logo192.png"
                                    alt="Socialixer"
                                    sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
                                />
                            </Box>
                            <IconButton
                                onClick={toggleSidebar}
                                sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary', bgcolor: alpha(theme.palette.divider, 0.15) } }}
                                title="Expand sidebar"
                            >
                                <ExpandIcon fontSize="small" />
                            </IconButton>
                            {/* Icon-only nav */}
                            <Navigation isSidebar={false} onCloseDrawer={() => {}} />
                        </Box>
                    ) : sidebarContent(toggleSidebar)}
                </Box>
            )}

            {/* Sidebar - Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: SIDEBAR_WIDTH,
                        bgcolor: theme.palette.background.paper,
                        backgroundImage: 'none'
                    },
                }}
            >
                {sidebarContent(handleDrawerToggle)}
            </Drawer>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: !isMobile ? (sidebarCollapsed ? 'calc(100% - 72px)' : `calc(100% - ${SIDEBAR_WIDTH}px)`) : '100%',
                    ml: !isMobile ? (sidebarCollapsed ? '72px' : `${SIDEBAR_WIDTH}px`) : 0,
                    transition: 'margin 0.3s ease, width 0.3s ease',
                }}
            >
                {/* TopBar */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        bgcolor: alpha(theme.palette.background.default, 0.7),
                        backdropFilter: 'blur(12px)',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                        color: theme.palette.text.primary,
                    }}
                >
                    <Toolbar sx={{ px: { xs: 2, sm: 4 }, gap: 2 }}>
                        {isMobile && (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}

                        <Box sx={{ flexGrow: 1 }} />

                        <IconButton onClick={onToggleTheme} sx={{ color: 'text.secondary' }}>
                            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>

                        <IconButton sx={{ color: 'text.secondary' }}>
                            <Badge badgeContent={3} color="primary" variant="dot">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* Subscription expiry / payment-failure banner */}
                <ExpiryBanner userPlan={userPlan} />

                {/* Dynamic Page Content */}
                <Box sx={{ p: { xs: 2, sm: 4, md: 6 }, maxWidth: '1600px', mx: 'auto' }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default MainLayout;
